import { Link } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const TEAM = [
  {
    name: "林晚",
    role: "主编 · 内容负责人",
    desc: "十年影视媒体从业者，专注华语剧集与类型片观察，负责频道内容方向与选题策划。",
    avatar:
      "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20confident%20young%20Chinese%20woman%20editor%20in%20a%20warm%20studio%20with%20soft%20red%20lighting%2C%20editorial%20magazine%20style%2C%20clean%20simple%20background%2C%20high%20detail&width=400&height=400&seq=hp-team-01&orientation=squarish",
  },
  {
    name: "陈默",
    role: "资深影评人",
    desc: "擅长剧情结构与叙事分析，负责深度解析栏目，用通俗的语言拆解复杂的故事逻辑。",
    avatar:
      "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20thoughtful%20young%20Chinese%20man%20film%20critic%20in%20a%20warm%20studio%20with%20soft%20amber%20lighting%2C%20editorial%20magazine%20style%2C%20clean%20simple%20background%2C%20high%20detail&width=400&height=400&seq=hp-team-02&orientation=squarish",
  },
  {
    name: "苏晴",
    role: "资讯编辑",
    desc: "负责影视行业动态与定档资讯的采集整理，追求消息的及时性与准确性。",
    avatar:
      "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20friendly%20young%20Chinese%20woman%20news%20editor%20in%20a%20warm%20studio%20with%20soft%20red%20lighting%2C%20editorial%20magazine%20style%2C%20clean%20simple%20background%2C%20high%20detail&width=400&height=400&seq=hp-team-03&orientation=squarish",
  },
];

const VALUES = [
  { icon: "ri-time-line", title: "及时", desc: "紧跟行业动态，第一时间为你带来影视定档与市场消息。" },
  { icon: "ri-search-eye-line", title: "深入", desc: "不止于资讯搬运，更提供有观点的剧情解析与内容观察。" },
  { icon: "ri-shield-check-line", title: "可靠", desc: "内容均经编辑核对整理，力求准确、克制的表达。" },
  { icon: "ri-heart-3-line", title: "热爱", desc: "我们首先是影迷，然后才是内容创作者。" },
];

export default function AboutPage() {
  useDocumentTitle(
    "关于我们 - 黑皮辣妹",
    "了解黑皮辣妹影视资讯与剧情介绍频道的定位、发展愿景、团队介绍与内容授权说明。",
  );

  return (
    <div className="hp-fade-up">
      <PageHeader
        title="关于我们"
        description="黑皮辣妹影视频道，专注影视资讯、剧情解析与作品资料，为影迷提供及时、深入、好读的内容。"
        breadcrumb={[{ label: "关于我们" }]}
        eyebrow="About Us"
      />

      <section className="w-full px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading font-black text-2xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                网站定位
              </h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-loose mb-4">
                黑皮辣妹是一个聚焦影视内容的信息频道，围绕影视资讯、剧情解析与作品资料三大板块，
                为影视爱好者、影评观众与追剧用户提供及时、深入、易读的内容。
                我们希望成为你了解影视动态、看懂一个好故事时，第一个想到的地方。
              </p>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-loose">
                从热门新片到口碑冷门，从定档消息到结局拆解，我们坚持用清晰的结构和克制的表达，
                帮助你在信息过载的时代里，快速找到真正想看的内容。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading font-black text-2xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                发展愿景
              </h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-loose mb-6">
                我们希望把黑皮辣妹做成一个“懂内容、也懂观众”的影视频道：
                既有速度，也有深度；既能陪你追剧，也能陪你聊片。
                未来，我们会持续丰富作品库与解析内容，让每一次浏览都有收获。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VALUES.map((v) => (
                  <div key={v.title} className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-200 p-4">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${v.icon} text-lg`} />
                    </span>
                    <span>
                      <span className="block font-heading font-bold text-foreground-950">{v.title}</span>
                      <span className="block text-xs text-foreground-500 mt-1 leading-relaxed">{v.desc}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading font-black text-2xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-secondary-500" />
                版权与授权
              </h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-loose">
                本站展示的作品资料、资讯与解析内容均为演示示范数据，仅用于产品展示，
                不代表任何真实影视作品或机构。文中涉及的图片均为 AI 生成的示意图。
                未经许可，请勿将本站内容用于商业用途。
              </p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-background-200 bg-background-100">
                <h3 className="font-heading font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-team-line text-secondary-500" />
                  团队简介
                </h3>
              </div>
              <ul className="divide-y divide-background-200">
                {TEAM.map((member) => (
                  <li key={member.name} className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-12 h-12 shrink-0 rounded-full overflow-hidden bg-background-200">
                        <img
                          src={member.avatar}
                          alt={member.name}
                          title={`${member.name} 团队成员`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span>
                        <span className="block font-heading font-bold text-foreground-950">{member.name}</span>
                        <span className="block text-xs text-primary-600">{member.role}</span>
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed">{member.desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-950 p-6 text-background-100">
              <h3 className="font-heading font-bold text-background-50 mb-2">想加入我们？</h3>
              <p className="text-sm text-background-200 leading-relaxed mb-4">
                我们长期欢迎内容创作者与影评作者供稿，一起把好内容做得更好。
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-medium px-5 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line" />
                联系我们
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}